import { Fragment, useState } from 'react';
import { useFormikContext } from 'formik';
import { Divider, Grid2, Typography } from '@mui/material';
import EditContentModal from '../global/EditContentModal';
import { ItemForm } from '../../types/items';
import useUser from '../../hooks/global/useUser';
import { getCommissionEmployees } from '../../helpers/items';
import TextInputControlled from '../form/base/TextInputControlled';

interface Props {
  open: boolean;
  onClose: () => void;
}

function ItemCommissionsModal({ open, onClose }: Props) {
  const { user } = useUser();
  const { values, setFieldValue } = useFormikContext<ItemForm>();

  const employeesAvailableForCommissions = getCommissionEmployees(
    user?.business?.employees,
  );

  const [employeeCommissions, setEmployeeCommissions] = useState<
    NonNullable<ItemForm['employeeCommissions']>
  >(
    (values.employeeCommissions || []).map(c => ({ ...c, rate: c.rate * 100 })),
  );

  const onDone = () => {
    setFieldValue(
      'employeeCommissions',
      employeesAvailableForCommissions.map(e => {
        const commission = employeeCommissions.find(
          c => c.employeeId === e.employeeId,
        );
        const percent = Number(commission?.rate || 0);
        const rate = Number.isNaN(percent) ? 0 : percent / 100;
        return {
          employeeId: e.employeeId,
          rate,
        };
      }),
    );
    onClose();
  };

  const onChangeCommission = (employeeId: string, rate: number) => {
    setEmployeeCommissions(prev => {
      const exists = prev.some(c => c.employeeId === employeeId);
      if (exists) {
        return prev.map(c => {
          if (c.employeeId === employeeId) {
            return { ...c, rate };
          }
          return c;
        });
      }
      return [...prev, { employeeId, rate }];
    });
  };

  return (
    <EditContentModal
      open={open}
      onClose={onClose}
      onDone={onDone}
      title="Item Commissions">
      {employeesAvailableForCommissions.map((employee, index) => {
        const rate = (
          employeeCommissions.find(c => c.employeeId === employee.employeeId)
            ?.rate || ''
        ).toString();
        return (
          <Fragment key={employee.employeeId}>
            <Grid2
              container
              columns={12}
              width="100%"
              justifyContent="space-between"
              alignItems="center"
              gap={{
                xs: 1,
                sm: 0,
              }}
              paddingX={2}
              paddingY={2.5}>
              <Grid2
                size={{
                  xs: 12,
                  sm: 3,
                }}
                order={{
                  xs: 1,
                  sm: 2,
                }}>
                <Typography variant="body1">
                  {employee.user?.firstName ||
                    employee.displayName ||
                    'Unnamed'}
                </Typography>
              </Grid2>

              <Grid2
                order={3}
                size={{
                  xs: 12,
                  sm: 7,
                }}
                flexWrap="nowrap"
                display="flex"
                alignItems="center"
                justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
                gap={2}>
                <TextInputControlled
                  id={`commission-${employee.employeeId}`}
                  onChange={newValue => {
                    onChangeCommission(
                      employee.employeeId,
                      newValue as unknown as number,
                    );
                  }}
                  type="number"
                  value={rate}
                  suffix="%"
                  step={0.1}
                  placeholder="0"
                />
              </Grid2>
            </Grid2>
            {index < employeesAvailableForCommissions.length - 1 && (
              <Divider
                color="#F2F2F2"
                variant="fullWidth"
                component="div"
                sx={{
                  borderColor: '#F2F2F2',
                }}
              />
            )}
          </Fragment>
        );
      })}
    </EditContentModal>
  );
}

export default ItemCommissionsModal;
